/* eslint-disable max-len */
/* eslint-disable no-use-before-define */
/* eslint-disable react/jsx-no-constructed-context-values */
import React, { ReactNode, createContext, useContext, useState } from 'react';
import { PriorAuthorizationDomain, ProcedureDetailDomain,
  PriorAuthorizationDomainDetails, ProviderDomain } from '../interfaces/PriorAuthorizationDomain';

interface PriorAuthorizationDomainContextInterface {
  priorAuthorizationDomain: PriorAuthorizationDomain;
  updatePriorAuthorizationDomain: (newData: PriorAuthorizationDomain) => void;
  updatePA: (name: string, value: any) => void;
  updateProcedureCodes: (index: number, procedure:any) => void;
  updateDiagnosisCodes: (index: number, procedure: any) => void;
  updateRequestingProvider: (index: any, provider:any) => void;
  isServiceProviderPhoneValid: boolean;
  setIsServiceProviderPhoneValid: (isValid: boolean) => void;
  filesUploadedValidationStatus: ValidationStatuses;
  updateFilesUploadedValidationStatus: (childId: string, isValid: boolean) => void;
  requestingProviderValidationStatuses: ValidationStatuses;
  updateRequestingProviderValidationStatus: (childId: string, isValid: boolean) => void;
  fieldValidationStatuses: ValidationStatuses;
  updateFieldValidationStatus: (key: string, isValid: boolean) => void;
  procedureValidationStatuses: ValidationStatuses;
  updateProcedureValidationStatus: (parentId:string, childId: string, isValid: boolean) => void;
  sameDiagnosticAddedStatuses: ValidationStatuses;
  updateDiagnosticValidationStatus: (childId: string, isValid: boolean) => void;
}

const defaultPriorAuthorizationDomainContextValues: PriorAuthorizationDomainContextInterface = {
  priorAuthorizationDomain: getDefaultDomainObject(),
  updatePriorAuthorizationDomain: () => { },
  updatePA: () => { },
  updateProcedureCodes: () => {},
  updateRequestingProvider: () => {},
  updateDiagnosisCodes: () => {},
  isServiceProviderPhoneValid: false,
  setIsServiceProviderPhoneValid: () => {},
  filesUploadedValidationStatus: {},
  updateFilesUploadedValidationStatus: () => {},
  requestingProviderValidationStatuses: {},
  updateRequestingProviderValidationStatus: () => {},
  fieldValidationStatuses: {},
  updateFieldValidationStatus: () => {},
  procedureValidationStatuses: {},
  updateProcedureValidationStatus: () => {},
  sameDiagnosticAddedStatuses: {},
  updateDiagnosticValidationStatus: () => {},
};

interface ValidationStatuses {
  [key: string]: boolean;
}

export const
  PriorAuthorizationDomainContext = createContext<PriorAuthorizationDomainContextInterface>(defaultPriorAuthorizationDomainContextValues);

export const usePADomainModel = () => useContext(PriorAuthorizationDomainContext);

export const PriorAuthorizationDomainContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [priorAuthorizationDomain,
    setPriorAuthorizationDomain] = useState<PriorAuthorizationDomain>(defaultPriorAuthorizationDomainContextValues.priorAuthorizationDomain);
  const [isServiceProviderPhoneValid, setIsServiceProviderPhoneValid] = useState<boolean>(false);
  const [requestingProviderValidationStatuses,
    setRequestingProviderValidationStatuses] = useState<ValidationStatuses>({});
  const [fieldValidationStatuses, setFieldValidationStatuses] = useState<ValidationStatuses>({});
  const [procedureValidationStatuses, setProceduresValidationStatuses] = useState<ValidationStatuses>({});
  const [sameDiagnosticAddedStatuses, setDiagnosticValidationStatuses] = useState<ValidationStatuses>({});
  const [filesUploadedValidationStatus, setFilesUploadValidationStatuses] = useState<ValidationStatuses>({});

  const updatePriorAuthorizationDomain = (newDomain: PriorAuthorizationDomain) => {
    if (newDomain) {
      setPriorAuthorizationDomain(newDomain);
    }
  };

  const updateRequestingProviderValidationStatus = (childId: string, isValid: boolean) => {
    setRequestingProviderValidationStatuses((prevStatuses) => ({
      ...prevStatuses,
      [childId]: isValid,
    }));
  };

  const updateDiagnosticValidationStatus = (childId: string, isValid: boolean) => {
    setDiagnosticValidationStatuses((prevStatuses) => ({
      ...prevStatuses,
      [childId]: isValid,
    }));
  };

  const updateFilesUploadedValidationStatus = (childId: string, isValid: boolean) => {
    setFilesUploadValidationStatuses((prevStatuses) => ({
      ...prevStatuses,
      [childId]: isValid,
    }));
  };

  const updateFieldValidationStatus = (key: string, isValid: boolean) => {
    setFieldValidationStatuses((prevStatuses) => ({
      ...prevStatuses,
      [key]: isValid,
    }));
  };

  const updateProcedureValidationStatus = (parentId: string, childId: string, isValid: boolean) => {
    setProceduresValidationStatuses((prevStatuses) => ({
      ...prevStatuses,
      [`${parentId}-${childId}`]: isValid,
    }));
  };
  const updatePA = (name: string, newData: any) => {
    setPriorAuthorizationDomain((currentDomain) => {
      const updatedDomain = { ...currentDomain };

      if (name.startsWith('form')) {
        const result = extractDetails(name);
        if (result && updatedDomain?.priorAuthorizationDomianDetails
        && result.index >= 0 && result.index < updatedDomain.priorAuthorizationDomianDetails.length) {
          updatedDomain.priorAuthorizationDomianDetails[result.index][result.name] = newData;
        }
      } else if (updatedDomain && updatedDomain.priorAuthorizationDomainHeader) {
        updatedDomain.priorAuthorizationDomainHeader[name] = newData;
      }

      return updatedDomain;
    });
  };

  const updateProcedureCodes = (index:number, newProcedure:Array<ProcedureDetailDomain>) => {
    setPriorAuthorizationDomain((currentDomain) => {
      const updatedDomain = { ...currentDomain };

      newProcedure.forEach((procedure, procedureIndex) => {
        if (updatedDomain.priorAuthorizationDomianDetails) {
          updatedDomain.priorAuthorizationDomianDetails[index].proceduresDetails.splice(procedureIndex, 1, procedure);
        }
      });
      return updatedDomain;
    });
  };

  const updateDiagnosisCodes = (index:number, newCodes:Array<string>) => {
    setPriorAuthorizationDomain((currentDomain) => {
      const updatedDomain = { ...currentDomain };
      if (updatedDomain && updatedDomain.priorAuthorizationDomianDetails) {
        updatedDomain.priorAuthorizationDomianDetails[index].additionalDiagnosis = newCodes;
      }
      return updatedDomain;
    });
  };
  const updateRequestingProvider = (index: number, newProvider:ProviderDomain) => {
    setPriorAuthorizationDomain((currentDomain) => {
      const updatedDomain = { ...currentDomain };

      if (updatedDomain.priorAuthorizationDomianDetails) {
        updatedDomain.priorAuthorizationDomianDetails[index].requestingProvider = newProvider;
      }
      return updatedDomain;
    });
  };

  function extractDetails(str: string) {
    const regex = /form(\d+)(\w+)/;
    const matches = str.match(regex);
    if (matches && matches.length >= 3) {
      const index = Number(matches[1]);
      const name = matches[2];
      return { index, name };
    }
    return null;
  }

  return (
    <PriorAuthorizationDomainContext.Provider value={{ updatePA,
      priorAuthorizationDomain,
      updatePriorAuthorizationDomain,
      updateProcedureCodes,
      updateRequestingProvider,
      updateDiagnosisCodes,
      isServiceProviderPhoneValid,
      setIsServiceProviderPhoneValid,
      filesUploadedValidationStatus,
      updateFilesUploadedValidationStatus,
      requestingProviderValidationStatuses,
      updateRequestingProviderValidationStatus,
      fieldValidationStatuses,
      updateFieldValidationStatus,
      procedureValidationStatuses,
      updateProcedureValidationStatus,
      sameDiagnosticAddedStatuses,
      updateDiagnosticValidationStatus }}
    >
      {children}
    </PriorAuthorizationDomainContext.Provider>
  );
};

export function getDefaultDomainObject(): PriorAuthorizationDomain {
  const authDetailsList: PriorAuthorizationDomainDetails[] = [];

  authDetailsList.push({
    procedureCode: '',
    levelOfUrgency: '',
    levelOfUrgencyDescription: '',
    authorizationType: '',
    typeOfService: '',
    typeOfServiceDescription: '',
    placeOfService: '',
    placeOfServiceDescription: '',
    diagnosisCode: '',
    lob: '',
    serviceRequestId: '',
    admissionStartDate: '',
    additionalDiagnosis: [],
    modifier: '',
    medicationNameNdcCode: '',
    adjustedDrugDoseDescription: '',
    medicationFrequency: '',
    medicationQuantity: '',
    hcpcUnitsPerDose: '',
    rxDose: '',
    medicationRoute: '',
    transplantZone: '',
    transplantDonorType: '',
    transplantOrganType: '',
    requestedDays: 0,
    proceduresDetails: [],
    requestingProvider: {
      taxId: '',
      cpfId: '',
      npi: '',
      lastName: '',
      firstName: '',
      groupName: '',
      facilityName: '',
      address: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      name: '',
    },
    cignaManaged: true,
  });

  const domainObj: PriorAuthorizationDomain = {
    groupingKey: '',
    precertificationId: '',
    transactionId: '',
    isAdditionalDocumentUpload: false,
    groupId: '',
    authorizationStatus: '',
    levelOfUrgency: '',
    priorAuthorizationId: '',
    sourceSystemId: '',
    requestSubmittedDate: '',
    priorAuthorizationDomainHeader: {
      healthPlan: '',
      partnerId: '',
      partnerName: '',
      userId: '',
      servicingProvider: {
        taxId: '',
        cpfId: '',
        npi: '',
        address: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
        phone: '',
        firstName: '',
        lastName: '',
        groupName: '',
        facilityName: '',
        name: '',
      },
      memberId: '',
      memberFirstName: '',
      memberLastName: '',
      memberDOB: '',
      memberGroupNumber: '',

    },
    priorAuthorizationDomianDetails: [],
  };
  domainObj.priorAuthorizationDomianDetails = authDetailsList;

  return domainObj;
}
